import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Badge, Box, IconButton, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MailIcon from '@mui/icons-material/Mail';
import { useTheme } from '@mui/material/styles';

import { DebouncedInputUI } from '../Interface';
import { getIcon } from '../../utils/helper';
import useUsers from '../../context/UsersContext';
import appService from '../../service/app.service';
import usePages from '../../context/PageContext';
import biService from '../../service/bi.service';
import dashboardService from '../../service/dashboard.service';
import newDocumentTypeService from '../../service/newDocumentType.service';
import { DocumentTypeRoutes } from '../../config/DocumentType';
import { saveSystemLog } from '../../utils/saveSystemLog';

const NavigationItem = ({ item, setSidebarOpen, filterSubMenuName }) => {
  const theme = useTheme();
  const ui = theme.palette.ui;
  const { user } = useUsers();
  const [visible, setVisible] = useState(false);
  const { setPages } = usePages();

  const handleToggleVisible = () => {
    setVisible((prevVisible) => !prevVisible);
  };

  const handleSubMenuClick = (submenu) => {
    setPages({ menuName: item.menuName, menuId: item.menuId, ...submenu });
    saveSystemLog({
      operation: 'Navigálás',
      description: `A(z) ${submenu.subMenuName} felületre lépett.`,
      createdBy: user.userId,
    });
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (filterSubMenuName) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [filterSubMenuName]);

  return (
    <div className="relative my-3 text-white cursor-pointer ml-3">
      <Box
        className="flex items-center justify-between hover:text-white rounded-md gap-x-3 px-2"
        sx={{
          '&:hover': {
            background: ui.navigationItemHover,
          },
        }}
        onClick={handleToggleVisible}
      >
        <p className="font-semibold text-md py-3">{item.menuName}</p>
        <div className="py-3 px-3">
          {visible === false ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m0 0l6.75-6.75M12 19.5l-6.75-6.75" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19.5v-15m0 0l-6.75 6.75M12 4.5l6.75 6.75" />
            </svg>
          )}
        </div>
      </Box>
      {visible && (
        <ul className="mt-1 space-y-1 px-6">
          {item.submenus.map((subItem) => (
            <Link to={subItem.url === '#' ? '/' : `/app/${subItem.url}`} className="block w-full">
              <Box
                sx={{
                  '&:hover': {
                    background: ui.navigationSubItemHover,
                  },
                }}
              >
                <li
                  onClick={() => handleSubMenuClick(subItem)}
                  key={subItem.subMenuName}
                  className="text-gray-400  hover:text-white group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
                >
                  {subItem.subMenuName}
                </li>
              </Box>
            </Link>
          ))}
        </ul>
      )}
    </div>
  );
};

const NavigationMenu = ({
  setSidebarOpen,
  isMobile,
  favouriteMenu = [],
  onFavNavigate,
  onLogout,
  messages,
  showMessageIcon,
  showExpiryCountIcon,
  expiryCount,
  handleNavigateExpiringPage,
  getMessages,
}) => {
  const [menuData, setMenuData] = useState([]);
  const [filteredMenuData, setFilteredMenuData] = useState([]);
  const [usersubMenuIds, setUsersubMenuIds] = useState([]);
  const [biReportIds, setBiReportIds] = useState([]);
  const [biReports, setBiReports] = useState([]);
  const [dashboardIds, setDashboardIds] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [newDocumentTypeIds, setNewDocumentTypeIds] = useState([]);
  const [newdocuments, setNewDocuments] = useState([]);
  const [filterSubMenuName, setFilterSubMenuName] = useState('');

  const { user, getUser } = useUsers();

  const fetchUserPermissions = (roleId) => {
    appService
      .getUserPermissions(roleId)
      .then((data) => {
        setUsersubMenuIds(data);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const fetchMenuData = () => {
    appService
      .getMenuData()
      .then((data) => {
        setMenuData(data);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const getBiRolePermission = (roleId) => {
    appService.getBiPermissions(roleId).then((data) => {
      setBiReportIds(data);
    });
  };

  const getNewDocumentTypeIds = (roleId) => {
    appService.getDocPermissions(roleId).then((data) => {
      setNewDocumentTypeIds(data);
    });
  };

  const getDashboardPermission = (roleId) => {
    appService.getDashboardPermissions(roleId).then((data) => {
      setDashboardIds(data);
    });
  };

  const getBiReports = () => {
    biService.allBiReports().then((data) => {
      setBiReports(data);
    });
  };

  const getDashboards = () => {
    dashboardService.allDashboards().then((data) => {
      setDashboards(data);
    });
  };

  const getNewDocuments = () => {
    newDocumentTypeService.allDocumentTypes().then((data) => {
      setNewDocuments(data);
    });
  };

  useEffect(() => {
    if (!Object.keys(user).length) return getUser();
    if (Object.keys(user).length > 0) {
      fetchUserPermissions(user.roleId);
      getBiRolePermission(user.roleId);
      getDashboardPermission(user.roleId);
      getNewDocumentTypeIds(user.roleId);
      fetchMenuData();
      getBiReports();
      getDashboards();
      getNewDocuments();
    }
  }, [user]);

  useEffect(() => {
    if (menuData.length > 0 && usersubMenuIds.length > 0) {
      const permittedNewDocuments = newdocuments.filter((doc) =>
        newDocumentTypeIds.some((docType) => docType.documentTypeId === doc.documentTypeId)
      );

      const directionalNewDocuments = permittedNewDocuments.filter((doc) => !doc.inventoryMovement);
      const internalNewDocuments = permittedNewDocuments.filter((doc) => doc.inventoryMovement);

      const filteredMenu = menuData
        .map((item) => {
          let biReportSubmenus = [];
          if (item.url === 'bi') {
            biReportSubmenus = biReports
              .filter((report) => biReportIds.some((biReportPermission) => biReportPermission.biReportId === report.id))
              .map((report) => ({
                subMenuId: uuidv4(),
                subMenuName: report.name,
                url: `bi/biPage/${report.id}`,
                parentMenuId: item.menuId,
                archive: 'N',
                isFormType: false,
              }));
          }

          let dashboardSubmenus = [];
          if (item.url === 'dashboard') {
            dashboardSubmenus = dashboards
              .filter((report) =>
                dashboardIds.some((dashboardPermission) => dashboardPermission.dashboardId === report.id)
              )
              .map((report) => ({
                subMenuId: uuidv4(),
                subMenuName: report.name,
                url: `dashboard/${report.id}`,
                parentMenuId: item.menuId,
                archive: 'N',
                isFormType: false,
              }));
          }

          let newDocumentSubmenus = [];
          if (item.url === 'document/incoming') {
            newDocumentSubmenus = directionalNewDocuments
              .filter((doc) => doc.direction === 'INCOMING')
              .map((document) => ({
                subMenuId: uuidv4(),
                subMenuName: document.name,
                url: `${DocumentTypeRoutes[document.documentTypeId]}`,
                parentMenuId: item.menuId,
                archive: 'N',
                isFormType: false,
              }));
          } else if (item.url === 'document/outgoing') {
            newDocumentSubmenus = directionalNewDocuments
              .filter((doc) => doc.direction === 'OUTGOING')
              .map((document) => ({
                subMenuId: uuidv4(),
                subMenuName: document.name,
                url: `${DocumentTypeRoutes[document.documentTypeId]}`,
                parentMenuId: item.menuId,
                archive: 'N',
                isFormType: false,
              }));
          } else if (item.url === 'document/internal') {
            newDocumentSubmenus = internalNewDocuments.map((document) => ({
              subMenuId: uuidv4(),
              subMenuName: document.name,
              url: `${DocumentTypeRoutes[document.documentTypeId]}`,
              parentMenuId: item.menuId,
              archive: 'N',
              isFormType: false,
            }));
          }

          const filteredSubmenus = item.submenus
            .filter((subItem) => usersubMenuIds.some((userSubMenu) => userSubMenu.subMenuId === subItem.subMenuId))
            .filter((subItem) => subItem.subMenuName.toLowerCase().includes(filterSubMenuName.toLowerCase()));

          const filteredBiReportSubmenus = biReportSubmenus.filter((subItem) =>
            subItem.subMenuName.toLowerCase().includes(filterSubMenuName.toLowerCase())
          );

          const filteredDashboardSubmenus = dashboardSubmenus.filter((subItem) =>
            subItem.subMenuName.toLowerCase().includes(filterSubMenuName.toLowerCase())
          );

          const filteredNewDocumentSubmenus = newDocumentSubmenus.filter((subItem) =>
            subItem.subMenuName.toLowerCase().includes(filterSubMenuName.toLowerCase())
          );

          return {
            ...item,
            submenus: [
              ...filteredSubmenus,
              ...filteredBiReportSubmenus,
              ...filteredDashboardSubmenus,
              ...filteredNewDocumentSubmenus,
            ],
          };
        })
        .filter((item) => item.submenus.length > 0);
      setFilteredMenuData(filteredMenu);
    }
    //88szor setel de működik -> promise all
  }, [
    menuData,
    usersubMenuIds,
    biReportIds,
    filterSubMenuName,
    biReports,
    dashboards,
    dashboardIds,
    newdocuments,
    newDocumentTypeIds,
  ]);

  return (
    <nav>
      <div className="px-5">
        <DebouncedInputUI
          label={<span className="text-white text-xl">Keresés</span>}
          sx={{
            '& .MuiInputBase-input': {
              color: '#fff',
            },
          }}
          debounceMs={800}
          setQuickFilterSearchValue={(newValue) => {
            setFilterSubMenuName(newValue);
          }}
          quickFilterSearchValue={filterSubMenuName}
        />
      </div>

      {isMobile && (
        <div className="px-5 mt-3 mb-2">
          <div className="flex items-center flex-wrap gap-2 mb-3">
            {favouriteMenu?.map((fav) => (
              <IconButton
                key={fav.id}
                sx={{ margin: 0, padding: 0 }}
                onClick={() => {
                  onFavNavigate?.(fav);
                  setSidebarOpen(false);
                }}
              >
                <Tooltip title={fav.name} placement="top">
                  {getIcon(fav.icon, '1.7rem', 'white')}
                </Tooltip>
              </IconButton>
            ))}
            <IconButton onClick={onLogout} sx={{ color: 'white', p: 0 }}>
              <Tooltip title={'Kijelentkezés'} placement="top">
                <LogoutIcon />
              </Tooltip>
            </IconButton>
            {showMessageIcon && (
              <Badge badgeContent={messages.count} sx={{ color: 'white' }}>
                <MailIcon
                  color="action"
                  className="cursor-pointer"
                  sx={{ color: 'white' }}
                  onClick={() => {
                    getMessages();
                    setSidebarOpen(false);
                  }}
                />
              </Badge>
            )}
            {showExpiryCountIcon && (
              <Badge
                badgeContent={expiryCount}
                sx={{ color: 'white', cursor: 'pointer' }}
                onClick={() => {
                  handleNavigateExpiringPage();
                  setSidebarOpen(false);
                }}
              >
                {getIcon('PriorityHigh', '1.5rem', '#E3242B')}
              </Badge>
            )}
          </div>
        </div>
      )}

      {filteredMenuData.map((item, index) => (
        <NavigationItem key={index} item={item} setSidebarOpen={setSidebarOpen} filterSubMenuName={filterSubMenuName} />
      ))}
    </nav>
  );
};

export default NavigationMenu;
